import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const foamConcentrates = [
  { name: "Aqueous Film Forming Foam (AFFF)", desc: "Effective on hydrocarbon fuel fires such as gasoline and diesel.", icon: "ri-water-flash-line" },
  { name: "Alcohol Resistant (AR-AFFF)", desc: "Designed for polar solvent fires; also works on hydrocarbon fuels.", icon: "ri-flask-line" },
  { name: "Synthetic Foam (Medium or High Expansion)", desc: "Detergent-based foam for large area coverage.", icon: "ri-test-tube-line" },
  { name: "Wetting Agent", desc: "Enhances water penetration for deep-seated Class A fires.", icon: "ri-drop-line" },
  { name: "Class A Foam", desc: "Used to suppress fires involving ordinary combustibles.", icon: "ri-leaf-line" },
  { name: "Protein Foam", desc: "Excellent film strength; commonly used in aviation facilities.", icon: "ri-shield-check-line" },
  { name: "Film Forming Fluoroprotein (FFFP)", desc: "Combines protein foam stability with film-forming properties.", icon: "ri-stack-line" },
  { name: "Fluoroprotein Foam", desc: "Highly effective on hydrocarbon fuel fires with excellent knockdown and burnback resistance.", icon: "ri-fire-line" },
];

const benefits = [
  {
    title: "Quickly Extinguishes Flammable Liquid Fires",
    desc: "Foam concentrates are the best suppression agent for fires resulting from combustible liquids like gasoline, kerosene, diesel and other oils. Foam effectively cools off the heat, smothers the flames and sucks off vapours from the surfaces, thereby putting out Class A and B fires quickly and safely.",
    icon: "ri-fire-line",
  },
  {
    title: "Conserves Water and Energy",
    desc: "Foam concentrates are the key ingredient in fire fighting foam, which requires just a little water. Besides, discharging the fire suppression agent from the storage devices does not require a significant amount of energy. All these not only make fire fighting foam more efficient but also very economical.",
    icon: "ri-leaf-line",
  },
  {
    title: "Less Water Damage",
    desc: "Unlike fire sprinklers or water-based fire extinguishers, fire fighting foam uses very little amounts of water. As a result, using a foam fire suppression system during fire emergencies will not cause any significant water damage to your documents and equipment in the building.",
    icon: "ri-shield-check-line",
  },
];

const foamWorkSteps = [
  { step: "1", title: "Create a Blanket", desc: "Creates a blanket over the fuel source to smother the flames and cut off oxygen supply." },
  { step: "2", title: "Separate the Fuel", desc: "Separates the fuel source from the surface on fire, preventing re-ignition." },
  { step: "3", title: "Cool the Surface", desc: "Cools off the fuel source and surfaces, reducing temperature below ignition point." },
  { step: "4", title: "Remove Vapours", desc: "Sucks off any remaining vapours on the surfaces to prevent the fire from re-ignition." },
];

export default function FireFightingFoamPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "All You Need to Know About Fire Fighting Foam Singapore",
            description:
              "A comprehensive guide to fire fighting foam — how it works, types of foam concentrates, common applications, and benefits for protecting commercial and industrial spaces in Singapore.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/92af08f3-be6b-4f59-afe3-d36b1ad4beb2_compressed_Depositphotos_26333619_l-2015.webp"
              alt="Intense flames and fire blaze showing the threat fire fighting foam suppresses"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Fire Fighting Foam</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Fire Suppression Agents
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              All You Need to Know About <span className="text-primary-400">Fire Fighting Foam</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Foam has been used as a key fire fighting medium for putting out combustible liquids for many years. Learn how it works, the types available, and its key benefits.
            </p>
          </div>
        </section>

        {/* What is Fire Fighting Foam */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                What Is It?
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                What is Fire Fighting Foam?
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                The foam that is used in fire fighting is a stable mass comprised of tiny bubbles of air with a lower density than water, gas or oil. The mass is made up of three key ingredients that include foam concentrate, water and air. When mixed in the correct proportions, the ingredients produce a homogeneous blanket called foam.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                The foam is then filled into pressurized devices or gas cylinders like fire extinguishers from where they are discharged to extinguish fires during an emergency.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                Foam is considered the primary fire suppression agent for ensuring better fire protection in areas where flammable liquids are processed, distributed or stored. The most common applications include aircraft hangers, oil and gas processing plants, oil storage warehouses, commercial cooking areas, loading platforms and other industrial spaces. Foam fire suppression is effective both indoors and outdoors, making it quite versatile.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/03ffa9f0-5101-4081-b041-2b654d8691d0_compressed_Depositphotos_71534781_l-2015-min.webp"
                alt="Portable fire extinguisher being held in hands showing detailed label and instructions"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                The Process
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                How Fire Fighting Foam <span className="text-primary-500">Works</span>
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                Foam works in stages to effectively extinguish and prevent re-ignition of flammable liquid fires.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {foamWorkSteps.map((s) => (
                <div key={s.step} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 mb-4">
                    <span className="text-background-50 font-bold text-base">{s.step}</span>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-base mb-2">{s.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-accent-50 border border-accent-200/70 rounded-2xl p-5 flex items-start gap-4 max-w-3xl mx-auto">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-information-line text-accent-600 text-lg"></i>
              </div>
              <p className="text-foreground-700 text-sm leading-relaxed">
                Fire fighting foam is effective on both hydrocarbon fuels (gasoline, jet fuel, kerosene, diesel) and polar solvent or alcohol fuels (alcohol-based combustible liquids). Unlike other fire suppression agents like water and CO2, foam extinguishes liquid fires in three phases: cooling off the flames, separating the fuel source from the surfaces, and smothering.
              </p>
            </div>
          </div>
        </section>

        {/* Foam Concentrate Types */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Types of Foam
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Common Types of Fire Fighting <span className="text-primary-500">Foam Concentrates</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-2xl mx-auto">
              There are various kinds of foam concentrates used in fire fighting systems. The particular type to choose will mainly depend on the potential fire risks on your building and the nature of the environment where it will be used.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {foamConcentrates.map((fc) => (
              <div key={fc.name} className="bg-background-100 rounded-2xl p-5 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                  <i className={`${fc.icon} text-primary-600 text-lg`}></i>
                </div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-2">{fc.name}</h3>
                <p className="text-foreground-600 text-xs leading-relaxed">{fc.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Advantages
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Benefits of Fire Fighting <span className="text-primary-500">Foam</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
              {benefits.map((b) => (
                <div key={b.title} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className={`${b.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{b.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Get Expert Advice on Fire Fighting Foam Systems
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            Ezzogenics provides quality fire protection systems including foam fire suppression systems for residential, commercial and industrial spaces across Singapore.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              Browse Fire Safety Products
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}